import { Form } from '@edgebox/react-components';
import { ContentRepresentation, TaxonomyFacet } from '@edgebox/sync-core-data-definitions';
import { IGovernanceStatsFilters, ISyncCoreApi, OpenApi } from '@edgebox/sync-core-rest-client';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Link } from 'react-router';
import { IFilterDropdownOption } from '../Onboarding/ContentInventory/Components/FilterDropdownList';
import { MultiLevelDropdownPanel } from '../Onboarding/ContentInventory/Components/MultiLevelDropdownPanel';
import { BP_POPOVER_CLASSES, BP_TOOLBAR_BUTTON_CLASSES, BP_TOOLBAR_BUTTON_OPEN_CLASSES } from './brandPresenceClasses';
import { SkeletonBlock } from './BrandPresenceSkeleton';
import { ContentHealthFacetConfig, healthBandOf, healthBandRanges, toScorePercentage } from './contentHealth.utils';
import { HEALTH_TINT_CLASSES } from './HealthScoreCell';
import { SectionLoadFailed, useRetry } from './SectionLoadFailed';
import { scheduleStatsRequest, StatsRequestPriority, statsRequestKey } from './statsRequestQueue';
import { useDismissOnOutsideClick } from './useDismissOnOutsideClick';

/** Said in place of the grid when its figures or the tags it is drawn for could not be loaded. */
const COVERAGE_LOAD_FAILED_MESSAGE = 'The coverage could not be loaded.';

/** Sync Core answers at most 200 cells: 20 rows by 10 columns. */
const MAX_ROW_TERMS = 20;
const MAX_COLUMN_TERMS = 10;

/** How many columns the grid opens with: the tags with the most pages. */
const DEFAULT_COLUMN_TERMS = 6;

/** The row facet the grid opens with, then the column facets it prefers, in order. */
const DEFAULT_ROW_FACET = TaxonomyFacet.Site;
const PREFERRED_COLUMN_FACETS: TaxonomyFacet[] = [TaxonomyFacet.AuthorityTopic, TaxonomyFacet.Product, TaxonomyFacet.Brand];

type Axis = 'rows' | 'columns';

type CoverageSelection = {
  rowFacet: TaxonomyFacet;
  columnFacet: TaxonomyFacet;
  rowTerms: string[];
  columnTerms: string[];
};

function defaultFacets(facets: ContentHealthFacetConfig[]): { rowFacet: TaxonomyFacet; columnFacet: TaxonomyFacet } | undefined {
  const row = facets.find((config) => config.facet === DEFAULT_ROW_FACET) || facets[0];
  const column =
    PREFERRED_COLUMN_FACETS.map((facet) => facets.find((config) => config.facet === facet && config.facet !== row?.facet)).find(
      (config): config is ContentHealthFacetConfig => !!config
    ) || facets.find((config) => config.facet !== row?.facet);

  return row && column ? { rowFacet: row.facet, columnFacet: column.facet } : undefined;
}

/**
 * The tags of a facet with the most pages first, up to the limit; tags without pages follow in
 * their list order so a small project still sees a full grid.
 */
async function topTermKeys({
  api,
  statsType,
  statsFilters,
  config,
  limit,
  signal,
}: {
  api: ISyncCoreApi;
  statsType: ContentRepresentation;
  statsFilters: IGovernanceStatsFilters;
  config: ContentHealthFacetConfig;
  limit: number;
  /** Aborted once the grid no longer wants the answer. */
  signal: AbortSignal;
}): Promise<string[]> {
  const filters = { ...statsFilters, facetTaxonomyKey: [config.taxonomyKey] };
  const totals = await scheduleStatsRequest({
    key: statsRequestKey({ method: 'contentStats.total', parameters: [statsType, filters] }),
    priority: StatsRequestPriority.ContentHealthCoverage,
    signal,
    run: () => api.governance.stats.total(statsType, filters),
  });
  const known = new Set(config.terms.map((term) => term.key));
  const ranked = (totals.terms || [])
    .filter((entry): entry is typeof entry & { term: string } => typeof entry.term === 'string' && known.has(entry.term))
    .sort((left, right) => right.count - left.count)
    .map((entry) => entry.term);

  return Array.from(new Set([...ranked, ...config.terms.map((term) => term.key)])).slice(0, limit);
}

function termName(config: ContentHealthFacetConfig | undefined, key: string): string {
  return config?.terms.find((term) => term.key === key)?.name ?? key;
}

/**
 * Coverage: how many pages carry a tag of one kind together with a tag of another — topics across
 * sites by default — each cell shaded by those pages' average health and opening them in Content
 * Inventory.
 */
export function ContentHealthCoverage({
  api,
  facets,
  statsType,
  statsFilters,
  countNoun,
  singularCountNoun,
  contentPathFor,
  onLoadingChange,
}: {
  api?: ISyncCoreApi;
  facets: ContentHealthFacetConfig[];
  statsType: ContentRepresentation;
  statsFilters: IGovernanceStatsFilters;
  countNoun: string;
  singularCountNoun: string;
  /** Where the items carrying the given tags are listed, or nothing where no list shows the figure's set. */
  contentPathFor: (termKeys: string[]) => string | undefined;
  onLoadingChange: (loading: boolean) => void;
}) {
  /** The kinds of tag the reader picked for the two axes; until they pick, the defaults. */
  const [chosenFacets, setChosenFacets] = React.useState<{ rowFacet: TaxonomyFacet; columnFacet: TaxonomyFacet } | undefined>(undefined);
  /** The axes the grid is drawn for, with their tags. It follows the chosen facets once their tags are in. */
  const [selection, setSelection] = React.useState<CoverageSelection | undefined>(undefined);
  /** The last grid that arrived, with the axes it was drawn for: it shows only while those are the axes asked for. */
  const [loadedGrid, setLoadedGrid] = React.useState<
    { matrix: OpenApi.GovernanceContentItemMatrixResponse; selection: CoverageSelection } | undefined
  >(undefined);
  const [failed, setFailed] = React.useState(false);
  const [attempt, retry] = useRetry();

  const configOf = React.useCallback((facet: TaxonomyFacet | undefined) => facets.find((config) => config.facet === facet), [facets]);

  // The grid renders without any setup: sites down the side, the topics with the most pages across.
  const wantedFacets = React.useMemo(() => chosenFacets ?? defaultFacets(facets), [chosenFacets, facets]);
  const wantedRowFacet = wantedFacets?.rowFacet;
  const wantedColumnFacet = wantedFacets?.columnFacet;
  /** The chosen facets' tags are still to be picked, so the grid on screen is not the one asked for. */
  const selectionPending =
    !!wantedRowFacet && !!wantedColumnFacet && (selection?.rowFacet !== wantedRowFacet || selection?.columnFacet !== wantedColumnFacet);

  // Picks the tags of a newly chosen facet: those with the most pages. An axis whose facet stayed
  // keeps the tags the reader picked for it.
  React.useEffect(() => {
    const rowConfig = configOf(wantedRowFacet);
    const columnConfig = configOf(wantedColumnFacet);
    if (!api || !selectionPending || !wantedRowFacet || !wantedColumnFacet || !rowConfig || !columnConfig) {
      return;
    }

    let active = true;
    // Aborted when the chosen facets or the filter change, or the section is left: a request not
    // sent yet is then dropped, and one on its way no longer holds a place for the current view.
    const superseded = new AbortController();
    onLoadingChange(true);
    setFailed(false);
    void (async () => {
      try {
        const [rowTerms, columnTerms] = await Promise.all([
          selection && selection.rowFacet === wantedRowFacet
            ? selection.rowTerms
            : topTermKeys({ api, statsType, statsFilters, config: rowConfig, limit: MAX_ROW_TERMS, signal: superseded.signal }),
          selection && selection.columnFacet === wantedColumnFacet
            ? selection.columnTerms
            : topTermKeys({ api, statsType, statsFilters, config: columnConfig, limit: DEFAULT_COLUMN_TERMS, signal: superseded.signal }),
        ]);
        if (active) {
          setSelection({ rowFacet: wantedRowFacet, columnFacet: wantedColumnFacet, rowTerms, columnTerms });
        }
      } catch {
        // Without both axes there is no grid, so the other read, if still waiting for its turn, is
        // dropped rather than sent for nothing.
        superseded.abort();
        if (active) {
          setFailed(true);
          onLoadingChange(false);
        }
      }
    })();

    return () => {
      active = false;
      superseded.abort();
    };
  }, [api, attempt, configOf, onLoadingChange, selection, selectionPending, statsFilters, statsType, wantedColumnFacet, wantedRowFacet]);

  React.useEffect(() => {
    const rowConfig = configOf(selection?.rowFacet);
    const columnConfig = configOf(selection?.columnFacet);
    if (
      !api ||
      !selection ||
      selectionPending ||
      !rowConfig ||
      !columnConfig ||
      !selection.rowTerms.length ||
      !selection.columnTerms.length
    ) {
      return;
    }

    let active = true;
    // See the tags above.
    const superseded = new AbortController();
    onLoadingChange(true);
    setFailed(false);
    const filters = {
      ...statsFilters,
      xAxisTaxonomyKey: rowConfig.taxonomyKey,
      yAxisTaxonomyKey: columnConfig.taxonomyKey,
      xAxisTerms: selection.rowTerms,
      yAxisTerms: selection.columnTerms,
    };
    scheduleStatsRequest({
      key: statsRequestKey({ method: 'contentStats.matrix', parameters: [statsType, filters] }),
      priority: StatsRequestPriority.ContentHealthCoverage,
      signal: superseded.signal,
      run: () => api.governance.stats.matrix(statsType, filters),
    })
      .then((result) => {
        if (active) {
          setLoadedGrid({ matrix: result, selection });
        }
      })
      .catch(() => {
        if (active) {
          setLoadedGrid(undefined);
          setFailed(true);
        }
      })
      .finally(() => {
        if (active) {
          onLoadingChange(false);
        }
      });

    return () => {
      active = false;
      superseded.abort();
    };
  }, [api, attempt, configOf, onLoadingChange, selection, selectionPending, statsFilters, statsType]);

  if (facets.length < 2) {
    return (
      <p className="m-0 py-6 text-center text-sm text-slate-500">
        Coverage compares two kinds of tags, for example topics across sites. Add a second kind of tag to see it.
      </p>
    );
  }

  const rowConfig = configOf(selection?.rowFacet);
  const columnConfig = configOf(selection?.columnFacet);
  // A grid drawn for other axes or other tags than the ones asked for is not shown under them.
  const matrix = loadedGrid && !selectionPending && loadedGrid.selection === selection ? loadedGrid.matrix : undefined;

  return (
    <div className="space-y-4">
      {selection ? (
        <CoverageControls
          facets={facets}
          selection={selection}
          chosenFacets={wantedFacets ?? selection}
          tagsLoading={selectionPending}
          onFacetsChange={setChosenFacets}
          onTermsChange={(next) => setSelection({ ...selection, ...next })}
        />
      ) : null}

      {failed ? (
        <SectionLoadFailed message={COVERAGE_LOAD_FAILED_MESSAGE} onRetry={retry} />
      ) : !matrix || !selection || !rowConfig || !columnConfig ? (
        <CoverageGridSkeleton />
      ) : (
        <CoverageGrid
          matrix={matrix}
          rowConfig={rowConfig}
          columnConfig={columnConfig}
          countNoun={countNoun}
          singularCountNoun={singularCountNoun}
          contentPathFor={contentPathFor}
        />
      )}
    </div>
  );
}

function CoverageControls({
  facets,
  selection,
  chosenFacets,
  tagsLoading,
  onFacetsChange,
  onTermsChange,
}: {
  facets: ContentHealthFacetConfig[];
  selection: CoverageSelection;
  /** The facets the reader picked, which the selects show while their tags are still loading. */
  chosenFacets: { rowFacet: TaxonomyFacet; columnFacet: TaxonomyFacet };
  /**
   * The tags of the chosen facets are still to arrive. The tag picker is closed and left out until
   * they do: it picks tags of the axes on screen, which are no longer the ones asked for.
   */
  tagsLoading: boolean;
  onFacetsChange: (next: { rowFacet: TaxonomyFacet; columnFacet: TaxonomyFacet }) => void;
  onTermsChange: (next: { rowTerms: string[]; columnTerms: string[] }) => void;
}) {
  const facetSelectClasses = 'inline-block w-auto';

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="m-0 flex flex-wrap items-center gap-2 text-sm text-slate-600">
        <span>Show</span>
        <Form.Select
          size="sm"
          className={facetSelectClasses}
          aria-label="Tag type in the columns"
          value={chosenFacets.columnFacet}
          onChange={(event) => onFacetsChange({ rowFacet: chosenFacets.rowFacet, columnFacet: event.target.value as TaxonomyFacet })}
        >
          {facets
            .filter((config) => config.facet !== chosenFacets.rowFacet)
            .map((config) => (
              <option key={`columns:${config.facet}`} value={config.facet}>
                {config.taxonomyName}
              </option>
            ))}
        </Form.Select>
        <span>across</span>
        <Form.Select
          size="sm"
          className={facetSelectClasses}
          aria-label="Tag type in the rows"
          value={chosenFacets.rowFacet}
          onChange={(event) => onFacetsChange({ rowFacet: event.target.value as TaxonomyFacet, columnFacet: chosenFacets.columnFacet })}
        >
          {facets
            .filter((config) => config.facet !== chosenFacets.columnFacet)
            .map((config) => (
              <option key={`rows:${config.facet}`} value={config.facet}>
                {config.taxonomyName}
              </option>
            ))}
        </Form.Select>
      </p>
      {tagsLoading ? null : <CoverageTagPicker facets={facets} selection={selection} onApply={onTermsChange} />}
    </div>
  );
}

function CoverageTagPicker({
  facets,
  selection,
  onApply,
}: {
  facets: ContentHealthFacetConfig[];
  selection: CoverageSelection;
  onApply: (next: { rowTerms: string[]; columnTerms: string[] }) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [axis, setAxis] = React.useState<Axis | undefined>(undefined);
  const [targetSearch, setTargetSearch] = React.useState('');
  const [valueSearch, setValueSearch] = React.useState('');
  const [draft, setDraft] = React.useState<{ rowTerms: string[]; columnTerms: string[] }>({ rowTerms: [], columnTerms: [] });
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const close = React.useCallback(() => {
    setOpen(false);
    setAxis(undefined);
    setTargetSearch('');
    setValueSearch('');
  }, []);
  useDismissOnOutsideClick({ open, containerRef, triggerRef, onDismiss: close });

  const rowConfig = facets.find((config) => config.facet === selection.rowFacet);
  const columnConfig = facets.find((config) => config.facet === selection.columnFacet);
  const axisConfig = axis === 'rows' ? rowConfig : columnConfig;
  const axisLimit = axis === 'rows' ? MAX_ROW_TERMS : MAX_COLUMN_TERMS;
  const draftKeys = axis === 'rows' ? draft.rowTerms : draft.columnTerms;

  const targets: IFilterDropdownOption[] = [
    { id: 'columns', label: columnConfig?.taxonomyName ?? '', badgeCount: draft.columnTerms.length },
    { id: 'rows', label: rowConfig?.taxonomyName ?? '', badgeCount: draft.rowTerms.length },
  ];
  const valueOptions: IFilterDropdownOption[] = (axisConfig?.terms || []).map((term) => ({ id: term.key, label: term.name }));
  const search = valueSearch.trim().toLowerCase();

  const toggle = (option: IFilterDropdownOption) => {
    const field = axis === 'rows' ? 'rowTerms' : 'columnTerms';
    setDraft((current) => {
      const keys = current[field];
      if (keys.includes(option.id)) {
        return { ...current, [field]: keys.filter((key) => key !== option.id) };
      }
      return keys.length >= axisLimit ? current : { ...current, [field]: [...keys, option.id] };
    });
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className={open ? BP_TOOLBAR_BUTTON_OPEN_CLASSES : BP_TOOLBAR_BUTTON_CLASSES}
        aria-expanded={open}
        onClick={() => {
          if (open) {
            close();
            return;
          }
          setDraft({ rowTerms: selection.rowTerms, columnTerms: selection.columnTerms });
          setAxis('columns');
          setOpen(true);
        }}
      >
        Choose tags…
        <FontAwesomeIcon icon={faChevronDown} className="text-[0.625rem]" aria-hidden="true" />
      </button>
      {open ? (
        <div className={`absolute right-0 top-full z-20 mt-2 w-[360px] ${BP_POPOVER_CLASSES}`}>
          <MultiLevelDropdownPanel
            selectedTarget={axis && axisConfig ? { id: axis, label: axisConfig.taxonomyName } : undefined}
            targetSearch={targetSearch}
            onTargetSearchChange={setTargetSearch}
            targetSearchPlaceholder="Search tag types"
            selectedTargetOptions={[]}
            filteredTargetOptions={targets.filter((target) => target.label.toLowerCase().includes(targetSearch.trim().toLowerCase()))}
            onSelectTarget={(option) => {
              setAxis(option.id === 'rows' ? 'rows' : 'columns');
              setValueSearch('');
            }}
            valueSearch={valueSearch}
            onValueSearchChange={setValueSearch}
            valueSearchPlaceholder="Search tags"
            selectedValueOptions={valueOptions.filter((option) => draftKeys.includes(option.id))}
            filteredValueOptions={valueOptions.filter((option) => !search || option.label.toLowerCase().includes(search))}
            valueSelectionMultiple={true}
            onToggleValue={toggle}
            hideSelectedValuesFromFiltered={true}
            changeTargetLabel="Rows or columns"
            onBackToTargets={() => {
              setAxis(undefined);
              setValueSearch('');
            }}
            onCancel={close}
            onApply={() => {
              onApply(draft);
              close();
            }}
            disableApply={!draft.rowTerms.length || !draft.columnTerms.length}
            emptyValuesMessage="No tag matches the search."
          />
          {axis ? <p className="m-0 mt-2 text-xs text-slate-500">{`Up to ${axisLimit} tags.`}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

function CoverageGrid({
  matrix,
  rowConfig,
  columnConfig,
  countNoun,
  singularCountNoun,
  contentPathFor,
}: {
  matrix: OpenApi.GovernanceContentItemMatrixResponse;
  rowConfig: ContentHealthFacetConfig;
  columnConfig: ContentHealthFacetConfig;
  countNoun: string;
  singularCountNoun: string;
  contentPathFor: (termKeys: string[]) => string | undefined;
}) {
  const legend = React.useMemo(() => healthBandRanges(), []);

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-xl ring-1 ring-slate-200/70">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th
                scope="col"
                className="sticky left-0 z-20 min-w-[12rem] border-b border-r border-slate-200/70 bg-white px-3 py-2.5 text-left text-xs font-semibold text-slate-500"
              >
                {`${rowConfig.taxonomyName} / ${columnConfig.taxonomyName}`}
              </th>
              {matrix.yAxisTerms.map((term) => (
                <th
                  key={`column:${term}`}
                  scope="col"
                  className="min-w-[7.5rem] border-b border-slate-200/70 bg-white px-2 py-2.5 text-left align-bottom text-xs font-semibold text-slate-600"
                >
                  <span className="line-clamp-2">{termName(columnConfig, term)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.rows.map((row) => {
              const rowName = termName(rowConfig, row.term);
              return (
                <tr key={`row:${row.term}`}>
                  <th
                    scope="row"
                    className="sticky left-0 z-10 border-b border-r border-slate-100 bg-white px-3 py-1.5 text-left text-sm font-semibold text-slate-800"
                  >
                    {rowName}
                  </th>
                  {row.cols.map((cell) => {
                    const percentage = toScorePercentage(cell.averageScore);
                    const columnName = termName(columnConfig, cell.term);
                    if (cell.count <= 0) {
                      return (
                        <td key={`cell:${row.term}:${cell.term}`} className="border-b border-slate-100 p-1">
                          <span className="flex h-11 items-center justify-center rounded-lg text-xs text-slate-400">
                            {`No ${singularCountNoun} yet`}
                          </span>
                        </td>
                      );
                    }

                    const noun = cell.count === 1 ? singularCountNoun : countNoun;
                    const target = contentPathFor([row.term, cell.term]);
                    const cellClassName = `flex h-11 flex-col items-center justify-center rounded-lg no-underline ${
                      typeof cell.averageScore !== 'number'
                        ? 'bg-slate-100 text-slate-700'
                        : HEALTH_TINT_CLASSES[healthBandOf(cell.averageScore)]
                    }`;
                    const title = `${rowName} and ${columnName}: ${cell.count} ${noun}${
                      percentage === undefined ? '' : `, average health ${percentage}%`
                    }`;
                    const figure = (
                      <>
                        <span className="text-sm font-bold tabular-nums leading-none">{cell.count}</span>
                        <span className="mt-0.5 text-[0.6875rem] leading-none">
                          <span className="opacity-80">{noun}</span>
                          {/* The shade says the health band; the figure says the health. Screen readers
                              hear it in the cell's description. */}
                          {percentage === undefined ? null : (
                            <span aria-hidden="true" className="font-semibold tabular-nums">{` · ${percentage}%`}</span>
                          )}
                        </span>
                      </>
                    );
                    const description = `with ${rowName} and ${columnName}${percentage === undefined ? '' : `, average health ${percentage}%`}`;

                    return (
                      <td key={`cell:${row.term}:${cell.term}`} className="border-b border-slate-100 p-1">
                        {target ? (
                          <Link
                            to={target}
                            title={title}
                            className={`${cellClassName} transition hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40`}
                          >
                            {figure}
                            <span className="sr-only">{`${description}. Show in Content Inventory`}</span>
                          </Link>
                        ) : (
                          <span title={title} className={cellClassName}>
                            {figure}
                            <span className="sr-only">{description}</span>
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
        <span>Shade: average health</span>
        {legend.map((range) => (
          <span key={range.band} className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className={`inline-block h-3 w-3 rounded ${HEALTH_TINT_CLASSES[range.band]}`} />
            {`${range.from}–${range.to}%`}
          </span>
        ))}
      </div>
    </div>
  );
}

function CoverageGridSkeleton() {
  return (
    <div className="space-y-2" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((row) => (
        <div key={row} className="grid grid-cols-[12rem_repeat(6,minmax(0,1fr))] gap-2">
          <SkeletonBlock className="h-11 w-full" />
          {[0, 1, 2, 3, 4, 5].map((cell) => (
            <SkeletonBlock key={cell} className="h-11 w-full rounded-lg" />
          ))}
        </div>
      ))}
    </div>
  );
}
